import React, { useEffect, useState } from 'react';
import { CardBody, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import classnames from "classnames";
import GeneralSettings from './SPOPGeneralSettings';
import CargoDischarging from './CargoDischarging';
import CargoCooling from './CargoCooling';
import CargoHeating from './CargoHeating';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import TankCleaning from './TankCleaning';
import ReeferContainer from './ReeferContainer';
import CargoReliquification from './CargoReliquification';
import { RootState } from 'index';
import { useSelector } from 'react-redux';
import { OnboardingStatus, Roles, SpOpsConfigurationConstant, VesselConfigrationTabs, VesselTypeConstant } from 'shared/constants';
import { useQuery } from 'react-query';
import { queryKeyes } from 'shared/queryKeys';
import { Get } from 'VesselMaster/vesselMaster.hooks'
import PopUp from 'Components/PopUp';
import SaveBeforeChange from 'VesselMaster/SaveBeforeChange';
import { FormikProps } from 'formik';
import { hasRole } from 'utils/auth/authUtils';

interface SpecialOperationsType {
    refreshVesselMachineries: number;
    setRefreshVesselMachineries: (value: number) => void;
    setCheckValuesBeforeSwitch: (value: boolean) => void;
    setTabName: (value: string) => void;
    setFormik: (value: FormikProps<any>) => void;
    formik: FormikProps<any>;
    VesselConfActiveTab: number;
}

const SpecialOperations = ({
    refreshVesselMachineries,
    setRefreshVesselMachineries,
    setCheckValuesBeforeSwitch,
    setTabName,
    setFormik,
    formik,
    VesselConfActiveTab,
}: SpecialOperationsType) => {
    /** Retrieves the VesselID and Vessels data from the Redux store. */
    const { VesselID, Vessels } = useSelector((state: RootState) => state.Reporting);
    /** State to track the currently active tab in the settings interface. */
    const [activeSettingsTab, setActiveSettingsTab] = useState('1');

    /** Finds the current vessel based on the VesselID. */
    const vessel = Vessels.find((rec: any) => rec.id === VesselID);

    /** States to manage the visibility of special operation tabs. */
    const [cargoDischarging, setCargoDischarging] = useState(false);
    const [cargoHeating, setCargoHeating] = useState(false);
    const [cargoCooling, setCargoCooling] = useState(false);
    const [refeerContainer, setReeferContainer] = useState(false);
    // const [tankCleaning, setTankCleaning] = useState(false);
    const [cargoReliquification, setCargoReliquification] = useState(false);
    const [count, setCount] = useState(0);
    const [checkVesselDataValuesBeforeSwitch, setCheckVesselDataValuesBeforeSwitch] = useState(false);
    const [vesselDataTabName, setVesselDataTabName] = useState("");
    const [state, setState] = useState(false);
    const [nextTab, setNextTab] = useState<number | null>(null);
    const [tabFormik, setTabFormik] = useState<FormikProps<any>>(null);
    const [refreshForm, setRefreshForm] = useState(false);
    const [refreshKey, setRefreshKey] = useState<number>(0);

    /**
     * Handles the tab change for special operations. It only updates the active tab
     * if the new tab is different from the current one, also increments the count state.
     * 
     * @param {string} tab - The tab identifier for switching views.
     */
    const toggle = (tab: any) => {
        const isSameTab = activeSettingsTab === tab;
        const isManagerWithOnboardedVessel =
            hasRole(Roles.MANAGER) && vessel.onboarding_status === OnboardingStatus.ONBOARDED;

        if (!isSameTab) {
            if (checkVesselDataValuesBeforeSwitch || isManagerWithOnboardedVessel) {
                setCount(count + 1);
                setActiveSettingsTab(tab);
            } else {
                setNextTab(tab);
                setState(true); // Show confirmation popup or handle unsaved changes
            }
            setRefreshForm((prev) => !prev);
        }
    };

    const handleSaveAndSwitchTab = async (formik: any) => {
        if (formik) {
            await formik.submitForm();
            formik.initialValues = formik.values;
            formik.resetForm({ values: formik.initialValues });
            await setRefreshKey(refreshKey + 1);
        }
        setCheckValuesBeforeSwitch(true);
        if (nextTab !== null) {
            setActiveSettingsTab(nextTab as any);
            setNextTab(null);
        }
        setState(false);
    };

    const handleNoSaveAndSwitchTab = async (formik: any) => {
        formik.resetForm({ values: formik.values });
        setCheckValuesBeforeSwitch(true);
        await setRefreshKey(refreshKey + 1);
        if (nextTab !== null) {
            setActiveSettingsTab(nextTab as any);
            setNextTab(null);
        }
        setState(false);
    };

    /**
     * Fetches the general settings object for the vessel's special operations from the backend.
     * Uses the vessel's ID as a parameter to load the corresponding settings.
     */
    const { data: generalSettingObj }: { data: any } = useQuery(
        [queryKeyes.vessel.SpecialOperationSettingList.key, VesselID],
        async () => {
            return await Get(queryKeyes.vessel.SpecialOperationSettingList.url(VesselID), VesselID);
        },
        {
            enabled: true,
            staleTime: Infinity,
        }
    );

    useEffect(() => {
        if (VesselConfActiveTab === VesselConfigrationTabs.SPECIAL_OPERATIONS && tabFormik?.values !== tabFormik?.initialValues) {
            setFormik(tabFormik);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps 
    }, [activeSettingsTab, VesselConfActiveTab, tabFormik])

    return (
        <React.Fragment>
            <PopUp
                state={state}
                setState={setState}
                body={
                    <SaveBeforeChange
                        tabName={vesselDataTabName}
                        onYes={() => handleSaveAndSwitchTab(tabFormik)} // Save and switch
                        onNo={() => handleNoSaveAndSwitchTab(tabFormik)} // Discard and switch
                    />
                }
                title="Alert"
                size="sm"
            />
            <CardBody className='pb-0 pt-0'>
                <p>Vessel Operations Configuration</p>
                <Nav tabs className='nav-tabs-custom nav-justified' key={refreshVesselMachineries}>
                    <NavItem>
                        <NavLink
                            style={{ cursor: "pointer" }}
                            className={classnames({
                                active: activeSettingsTab === SpOpsConfigurationConstant.SpOpsGeneralSettingsId,
                            })}
                            onClick={() => {
                                toggle(SpOpsConfigurationConstant.SpOpsGeneralSettingsId);
                            }}
                        >
                            General Settings
                        </NavLink>
                    </NavItem>
                    {vessel && vessel?.vessel_type_precedence_id === VesselTypeConstant.TANKER &&
                        <React.Fragment>
                            {(cargoHeating || (generalSettingObj && generalSettingObj[0]?.cargo_heating)) &&
                                <NavItem>
                                    <NavLink
                                        style={{ cursor: "pointer" }}
                                        className={classnames({
                                            active: activeSettingsTab === SpOpsConfigurationConstant.SpOpsCargoHeatingId,
                                        })}
                                        onClick={() => {
                                            toggle(SpOpsConfigurationConstant.SpOpsCargoHeatingId);
                                        }}
                                    >
                                        Cargo Heating
                                    </NavLink>
                                </NavItem>}
                            {(cargoDischarging || (generalSettingObj && generalSettingObj[0]?.cargo_discharging)) &&
                                <NavItem>
                                    <NavLink
                                        style={{ cursor: "pointer" }}
                                        className={classnames({
                                            active: activeSettingsTab === SpOpsConfigurationConstant.SpOpsCargoDischargingId,
                                        })}
                                        onClick={() => {
                                            toggle(SpOpsConfigurationConstant.SpOpsCargoDischargingId);
                                        }}
                                    >
                                        Cargo Discharging
                                    </NavLink>
                                </NavItem>
                            }
                            {/* {(tankCleaning || (generalSettingObj && generalSettingObj[0]?.tank_cleaning)) &&
                                <NavItem>
                                    <NavLink
                                        style={{ cursor: "pointer" }}
                                        className={classnames({
                                            active: activeSettingsTab === SpOpsConfigurationConstant.SpOpsTankCleaningId,
                                        })}
                                        onClick={() => {
                                            toggle(SpOpsConfigurationConstant.SpOpsTankCleaningId);
                                        }}
                                    >
                                        Tank Cleaning
                                    </NavLink>
                                </NavItem>
                            } */}
                        </React.Fragment>
                    }
                    {vessel && (vessel?.vessel_type_precedence_id === VesselTypeConstant.GAS_CARRIER ||
                        vessel?.vessel_type_precedence_id === VesselTypeConstant.LNG_CARRIER) &&
                        <React.Fragment>
                            {(cargoCooling || (generalSettingObj && generalSettingObj[0]?.cargo_cooling)) &&
                                <NavItem>
                                    <NavLink
                                        style={{ cursor: "pointer" }}
                                        className={classnames({
                                            active: activeSettingsTab === SpOpsConfigurationConstant.SpOpsCargoCoolingId,
                                        })}
                                        onClick={() => {
                                            toggle(SpOpsConfigurationConstant.SpOpsCargoCoolingId);
                                        }}
                                    >
                                        Cargo Cooling
                                    </NavLink>
                                </NavItem>}
                            {(cargoReliquification || (generalSettingObj && generalSettingObj[0]?.cargo_reliquification)) &&
                                <NavItem>
                                    <NavLink
                                        style={{ cursor: "pointer" }}
                                        className={classnames({
                                            active: activeSettingsTab === SpOpsConfigurationConstant.SpOpsCargoReliquificationId,
                                        })}
                                        onClick={() => {
                                            toggle(SpOpsConfigurationConstant.SpOpsCargoReliquificationId);
                                        }}
                                    >
                                        Cargo Reliquification
                                    </NavLink>
                                </NavItem>
                            }
                        </React.Fragment>
                    }
                    {vessel && (vessel?.vessel_type_precedence_id === VesselTypeConstant.CONTAINER ||
                        vessel?.vessel_type_precedence_id === VesselTypeConstant.GENERAL_CARGO_SHIP) &&
                        <React.Fragment>
                            {(refeerContainer || (generalSettingObj && generalSettingObj[0]?.reefer_container)) &&
                                <NavItem>
                                    <NavLink
                                        style={{ cursor: "pointer" }}
                                        className={classnames({
                                            active: activeSettingsTab === SpOpsConfigurationConstant.SpOpsReeferContainerId,
                                        })}
                                        onClick={() => {
                                            toggle(SpOpsConfigurationConstant.SpOpsReeferContainerId);
                                        }}
                                    >
                                        Reefer Container
                                    </NavLink>
                                </NavItem>
                            }
                        </React.Fragment>
                    }
                </Nav>
            </CardBody>
            <TabContent activeTab={activeSettingsTab} className="p-3 text-muted">
                <TabPane tabId={SpOpsConfigurationConstant.SpOpsGeneralSettingsId}>
                    <GeneralSettings
                        setCargoDischarging={setCargoDischarging}
                        setCargoHeating={setCargoHeating}
                        setCargoCooling={setCargoCooling}
                        setReeferContainer={setReeferContainer}
                        setCargoReliquification={setCargoReliquification}
                        // setTankCleaning={setTankCleaning}
                        count={count}
                        setCount={setCount}
                        refreshVesselMachineries={refreshVesselMachineries}
                        setRefreshVesselMachineries={setRefreshVesselMachineries}
                        setCheckVesselDataValuesBeforeSwitch={setCheckVesselDataValuesBeforeSwitch}
                        setVesselDataTabName={setVesselDataTabName}
                        setCheckValuesBeforeSwitch={setCheckValuesBeforeSwitch}
                        setTabName={setTabName}
                        setFormik={setFormik}
                        formik={formik}
                        activeTab={activeSettingsTab}
                        setTabFormik={setTabFormik}
                        refreshForm={refreshForm}
                        refreshKey={refreshKey}
                    />
                </TabPane>
                <TabPane tabId={SpOpsConfigurationConstant.SpOpsCargoDischargingId}>
                    <CargoDischarging
                        setCheckVesselDataValuesBeforeSwitch={setCheckVesselDataValuesBeforeSwitch}
                        setVesselDataTabName={setVesselDataTabName}
                        setCheckValuesBeforeSwitch={setCheckValuesBeforeSwitch}
                        setTabName={setTabName}
                        setFormik={setFormik}
                        formik={formik}
                        activeTab={activeSettingsTab}
                        setTabFormik={setTabFormik}
                        refreshForm={refreshForm}
                        refreshVesselMachineries={refreshVesselMachineries}
                        refreshKey={refreshKey}
                    />
                </TabPane>
                <TabPane tabId={SpOpsConfigurationConstant.SpOpsCargoCoolingId}>
                    <CargoCooling
                        setCheckVesselDataValuesBeforeSwitch={setCheckVesselDataValuesBeforeSwitch}
                        setVesselDataTabName={setVesselDataTabName}
                        setCheckValuesBeforeSwitch={setCheckValuesBeforeSwitch}
                        setTabName={setTabName}
                        setFormik={setFormik}
                        formik={formik}
                        activeTab={activeSettingsTab}
                        setTabFormik={setTabFormik}
                        refreshForm={refreshForm}
                        refreshVesselMachineries={refreshVesselMachineries}
                        refreshKey={refreshKey}
                    />
                </TabPane>
                <TabPane tabId={SpOpsConfigurationConstant.SpOpsCargoHeatingId}>
                    <CargoHeating
                        setCheckVesselDataValuesBeforeSwitch={setCheckVesselDataValuesBeforeSwitch}
                        setVesselDataTabName={setVesselDataTabName}
                        setCheckValuesBeforeSwitch={setCheckValuesBeforeSwitch}
                        setTabName={setTabName}
                        setFormik={setFormik}
                        formik={formik}
                        activeTab={activeSettingsTab}
                        setTabFormik={setTabFormik}
                        refreshForm={refreshForm}
                        refreshVesselMachineries={refreshVesselMachineries}
                        refreshKey={refreshKey}
                    />
                </TabPane>
                {/* <TabPane tabId={SpOpsConfigurationConstant.SpOpsTankCleaningId}>
                    <TankCleaning
                        setCheckVesselDataValuesBeforeSwitch={setCheckVesselDataValuesBeforeSwitch}
                        setVesselDataTabName={setVesselDataTabName}
                        setCheckValuesBeforeSwitch={setCheckValuesBeforeSwitch}
                        setTabName={setTabName}
                        setFormik={setFormik}
                        formik={formik}
                        activeTab={activeSettingsTab}
                        setTabFormik={setTabFormik}
                        refreshForm={refreshForm}
                        refreshVesselMachineries={refreshVesselMachineries}
                    />
                </TabPane> */}
                <TabPane tabId={SpOpsConfigurationConstant.SpOpsReeferContainerId}>
                    <ReeferContainer
                        setCheckVesselDataValuesBeforeSwitch={setCheckVesselDataValuesBeforeSwitch}
                        setVesselDataTabName={setVesselDataTabName}
                        setCheckValuesBeforeSwitch={setCheckValuesBeforeSwitch}
                        setTabName={setTabName}
                        setFormik={setFormik}
                        formik={formik}
                        activeTab={activeSettingsTab}
                        setTabFormik={setTabFormik}
                        refreshForm={refreshForm}
                        refreshVesselMachineries={refreshVesselMachineries}
                        refreshKey={refreshKey}
                    />
                </TabPane>
                <TabPane tabId={SpOpsConfigurationConstant.SpOpsCargoReliquificationId}>
                    <CargoReliquification
                        setCheckVesselDataValuesBeforeSwitch={setCheckVesselDataValuesBeforeSwitch}
                        setVesselDataTabName={setVesselDataTabName}
                        setCheckValuesBeforeSwitch={setCheckValuesBeforeSwitch}
                        setTabName={setTabName}
                        setFormik={setFormik}
                        formik={formik}
                        activeTab={activeSettingsTab}
                        setTabFormik={setTabFormik}
                        refreshForm={refreshForm}
                        refreshVesselMachineries={refreshVesselMachineries}
                        refreshKey={refreshKey}
                    />
                </TabPane>
            </TabContent>
        </React.Fragment>
    )
}

export default SpecialOperations;